import { useEffect, useMemo } from 'react';

import { useMainSubscriptionStore } from '../../store/useMainSubscriptionStore';

import { useMySubscriptionsQuery } from './react-query/useMySubscriptionsQuery';
import useMeQuery from './react-query/user/useMeQuery';
import { useHistoryMethod } from './useHistoryMethod';

/**
 * 루트 페이지(/) 리다이렉션 로직을 담당하는 커스텀 훅
 *
 * 리다이렉션 우선순위:
 * 1. 비로그인 사용자 → /main (ManualPage)
 * 2. 정기배송 이력 있음 → /subscriptions/{id}/main (대표 구독 우선)
 * 3. 정기배송 이력 없음 또는 에러 → /main (Graceful Degradation)
 */
export const useRootPageRedirection = () => {
  const history = useHistoryMethod();

  const { me, isLoading: isLoadingMe } = useMeQuery();
  const {
    data: subscriptions = [],
    isSuccess: isSuccessSubscriptions,
    isError: isErrorSubscriptions,
  } = useMySubscriptionsQuery();

  const mainSubscriptionId = useMainSubscriptionStore.use.mainSubscriptionId();
  const setMainSubscriptionId =
    useMainSubscriptionStore.use.setMainSubscriptionId();

  // 대표 구독이 유효한지 확인
  const hasValidMainSubscription = useMemo(() => {
    if (!mainSubscriptionId || subscriptions.length === 0) return false;
    return subscriptions.some((sub) => sub.id === mainSubscriptionId);
  }, [mainSubscriptionId, subscriptions]);

  // 리다이렉션 타겟 경로 계산
  const targetPath = useMemo(() => {
    // 로딩 중: 대기
    if (isLoadingMe) return null;

    // 비로그인: ManualPage
    if (!me) return '/main';

    // 정기배송 쿼리 대기 중
    if (!isSuccessSubscriptions && !isErrorSubscriptions) return null;

    // 정기배송 이력 없음 또는 에러: ManualPage (Graceful Degradation)
    if (subscriptions.length === 0 || isErrorSubscriptions) return '/main';

    // 정기배송 이력 있음: SubscriptionMainPage
    const resolvedSubscriptionId = hasValidMainSubscription
      ? mainSubscriptionId
      : subscriptions[0].id;

    return `/subscriptions/${resolvedSubscriptionId}/main`;
  }, [
    isLoadingMe,
    me,
    isSuccessSubscriptions,
    isErrorSubscriptions,
    subscriptions,
    hasValidMainSubscription,
    mainSubscriptionId,
  ]);

  // 대표 구독 자동 설정 (구독이 있지만 대표 구독이 설정되지 않은 경우)
  useEffect(() => {
    if (subscriptions.length === 0 || hasValidMainSubscription) return;

    setMainSubscriptionId(subscriptions[0].id);
  }, [subscriptions, setMainSubscriptionId, hasValidMainSubscription]);

  // 리다이렉션 실행
  useEffect(() => {
    if (!targetPath) return;

    history.replace(targetPath);
  }, [targetPath, history]);
};
